import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDashboardKpis, type DashboardChat, type DashboardSnapshot } from '../api/reporting.js';
import { ApiError } from '../api/apiClient.js';

/**
 * #155 — Vista Dashboard + Live Ops para tenant-admin.
 * Consume GET /dashboard/kpis del reporting-api (#153).
 * Auto-refresh cada 30s.
 */
export function TenantDashboard(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();

  const { data, isLoading, error } = useQuery<DashboardSnapshot, ApiError>({
    queryKey: ['dashboard-kpis', id],
    queryFn: () => getDashboardKpis(25),
    enabled: Boolean(id),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  if (isLoading) {
    return <p className="text-slate-500">Cargando dashboard…</p>;
  }
  if (error) {
    return <p className="text-red-700">Error: {error.message}</p>;
  }
  if (!data) {
    return <p className="text-slate-500">Sin datos.</p>;
  }

  const m = data.metrics;
  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Dashboard · {id}</h2>
          <p className="text-xs text-slate-500">
            Actualizado: {new Date(data.generatedAt).toLocaleString()} (auto-refresh 30s)
          </p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiCard label="Mensajes recibidos" value={m.msgs_received} tone="brand" />
        <KpiCard label="Flujos completados" value={m.flows_completed} tone="emerald" />
        <KpiCard label="Handoffs" value={m.handoffs_requested} tone="amber" />
        <KpiCard label="NPS prom." value={m.nps_avg !== null ? m.nps_avg.toFixed(1) : '—'} tone="indigo" />
        <KpiCard label="Outbound enviados" value={m.outbound_sent} tone="emerald" />
        <KpiCard label="Outbound fallidos" value={m.outbound_failed} tone="rose" />
        <KpiCard label="Surveys iniciadas" value={m.surveys_started} tone="indigo" />
        <KpiCard label="Surveys completadas" value={m.surveys_completed} tone="emerald" />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChatTable
          title={`Chats activos (${data.activeChats.length})`}
          empty="No hay conversaciones activas."
          rows={data.activeChats}
        />
        <ChatTable
          title={`En cola (${data.queueChats.length})`}
          empty="No hay conversaciones en cola."
          rows={data.queueChats}
        />
      </section>

      {data.note && (
        <p className="text-xs text-slate-500 italic">
          Nota: {data.note}
        </p>
      )}
    </div>
  );
}

type Tone = 'brand' | 'emerald' | 'amber' | 'rose' | 'indigo';
const TONE_BG: Record<Tone, string> = {
  brand: 'bg-brand-50 text-brand-900 border-brand-200',
  emerald: 'bg-emerald-50 text-emerald-900 border-emerald-200',
  amber: 'bg-amber-50 text-amber-900 border-amber-200',
  rose: 'bg-rose-50 text-rose-900 border-rose-200',
  indigo: 'bg-indigo-50 text-indigo-900 border-indigo-200',
};

function KpiCard({ label, value, tone }: { label: string; value: number | string; tone: Tone }): JSX.Element {
  return (
    <div className={`rounded-lg border p-3 ${TONE_BG[tone]}`}>
      <div className="text-xs uppercase tracking-wide opacity-70">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}

function ChatTable({
  title,
  empty,
  rows,
}: {
  title: string;
  empty: string;
  rows: DashboardChat[];
}): JSX.Element {
  return (
    <div className="rounded border border-slate-200 bg-white">
      <header className="border-b border-slate-100 px-3 py-2 text-sm font-medium">{title}</header>
      {rows.length === 0 ? (
        <p className="p-4 text-sm text-slate-500">{empty}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Conversación</th>
              <th className="px-3 py-2">Canal</th>
              <th className="px-3 py-2">Flow</th>
              <th className="px-3 py-2">Actualizado</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.conversationId} className="border-t border-slate-100">
                <td className="px-3 py-2 font-mono text-xs">{r.conversationId}</td>
                <td className="px-3 py-2">{r.channel}</td>
                <td className="px-3 py-2">{r.flowId}</td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {r.updatedAt ? new Date(r.updatedAt).toLocaleTimeString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
